import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { HypotheticalPriceComponent, SpaceBlockingForm } from 'components/SpaceBooking/SpaceBlocking';
import { SpaceBookingEditForm } from 'components/SpaceBooking/SpaceBooking/SpaceBookingEditForm';
import { useRefetchContext } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import { useFitText } from 'hooks/useFitText';
import { useModal } from 'hooks/useModal';
import { CSSProperties, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { useSelector } from 'react-redux';
import { AppState } from 'redux/store';

export const BlockingCell = ({
    blocking,
    vertical = false,
    isOwner = false
}: {
    blocking: MappedSpace['blockings'][0];
    vertical?: boolean;
    isOwner?: boolean;
}) => {
    const isAuthenticated = useSelector((state: AppState) => state.auth.isAuthenticated);
    const [setModal, resetModal] = useModal();
    const clubSlug = useClubSlug();
    const { refetchAll, refetchBlocking, refetchBooking } = useRefetchContext();
    const cellRef = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag] = useDrag({
        type: blocking.type,
        canDrag: () => isOwner,
        item: {
            id: blocking.id,
            start_time: blocking.start_time,
            end_time: blocking.end_time,
            type: blocking.type,
            repeat_until: blocking.repeat_until,
            repeat_period: blocking.repeat_period
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    const handleClick = () => {
        if (isOwner) {
            // TODO check if ownClub or user owns booking
            switch (blocking.type.toUpperCase()) {
                case 'BOOKING':
                    setModal({
                        title: 'Buchung bearbeiten',
                        content: (
                            <SpaceBookingEditForm
                                bookingId={blocking.id}
                                clubSlug={clubSlug}
                                callback={(booking) => {
                                    resetModal();
                                    refetchAll();
                                    refetchBooking(booking);
                                }}
                            />
                        ),
                        show: true
                    });
                    break;

                case 'BLOCKING':
                    setModal({
                        title: 'Blockierung bearbeiten',
                        content: (
                            <SpaceBlockingForm
                                blocking={blocking as any}
                                clubSlug={clubSlug}
                                fetch
                                callback={(blocking) => {
                                    resetModal();
                                    refetchAll();
                                    refetchBlocking(blocking);
                                }}
                            />
                        ),
                        show: true
                    });
                    break;

                default:
                    break;
            }

            return;
        }

        if (blocking.is_own) {
            setModal({
                title: 'Buchung bearbeiten',
                content: (
                    <SpaceBookingEditForm
                        bookingId={blocking.id}
                        clubSlug={clubSlug}
                        callback={(booking) => {
                            resetModal();
                            refetchAll();
                            refetchBooking(booking);
                        }}
                    />
                ),
                show: true
            });
        }
    };

    const size = (blocking.end_time - blocking.start_time) * 25 - 1;
    const color = blocking.color || (blocking.repeat_until ? '#4dc0b5' : '#38c172');

    useFitText(cellRef, { minFontSize: 10, maxFontSize: 14 });

    const dynamicStyle: CSSProperties = {
        borderTopWidth: 4,
        borderTopStyle: 'solid',
        pointerEvents: isDragging ? 'none' : 'auto',
        borderTopColor: color,
        background: blocking.is_own ? '#FDF7E6' : undefined,
        [vertical ? 'height' : 'width']: size
    };

    return (
        <Tooltip
            placement="top"
            tooltip={
                <div className="tw-p-2">
                    {!isOwner ? (
                        <>
                            <h5 className="tw-m-0">{blocking.reason}</h5>
                            {blocking.type === 'BLOCKING' ? (
                                <p className="tw-m-0">{blocking.description}</p>
                            ) : isAuthenticated && blocking.fellow_names?.length > 0 ? (
                                <p className="tw-m-0">{blocking.fellow_names.join(', ')}</p>
                            ) : (
                                <small className="tw-text-accents-5">Keine Mitspieler</small>
                            )}
                        </>
                    ) : (
                        <>
                            <h5 className="tw-m-0">{blocking.reason}</h5>
                            {blocking.type === 'BLOCKING' ? (
                                <>
                                    <p className="tw-m-0">{blocking.remarks || 'Keine Anmerkungen'}</p>
                                    <HypotheticalPriceComponent withSpacer blockingId={blocking.id} />
                                </>
                            ) : blocking.fellow_names?.length > 0 ? (
                                <p className="tw-m-0">{blocking.fellow_names.join(', ')}</p>
                            ) : (
                                <small className="tw-text-accents-5">Keine Mitspieler</small>
                            )}
                        </>
                    )}
                </div>
            }
        >
            <div
                style={dynamicStyle}
                ref={(ref) => {
                    if (ref) drag(ref);
                }}
                onClick={handleClick}
                className="tw-relative tw-z-[2] tw-flex tw-h-full tw-cursor-pointer tw-justify-center tw-break-words tw-border-t-[5px] tw-border-solid tw-bg-white tw-p-2 tw-pb-4 tw-pt-4 tw-shadow-md"
            >
                <span
                    ref={cellRef}
                    className="tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-px-1 tw-text-center"
                >
                    {blocking.reason}
                </span>

                <small className="tw-text-secondary tw-absolute tw-bottom-0 tw-left-[0.2rem] tw-right-[0.2rem] tw-flex tw-justify-between tw-text-[65%] tw-italic">
                    {timeDivisions.times[blocking.start_time]}
                    {blocking.is_own && <span>Meine Buchung</span>}
                    {(blocking.description || (isOwner && blocking.remarks)) && (
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    )}
                </small>
            </div>
        </Tooltip>
    );
};
